import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Loader2, Clock, ArrowRight, ArrowLeft, Sparkles, CheckCircle2, Lock, Flag, ListChecks } from "lucide-react";
import { ReviewScreen, StrikeButton, struckStyle, useStrikes } from "@/components/test-section/ExamTools";
import HighlightableText from "@/components/HighlightableText";
import FigureView from "@/components/test-section/FigureView";
import { scaledScore } from "@/components/test-section/meta";

const SECTION_META = {
  cp: { short: "C/P", tint: "var(--primary)" },
  cars: { short: "CARS", tint: "var(--accent)" },
  bb: { short: "B/B", tint: "var(--hl-blue)" },
  ps: { short: "P/S", tint: "var(--weakness)" },
};

function SectionProgress({ sec }) {
  const passPct = Math.min(100, Math.round((sec.passages_ready / Math.max(1, sec.passages_target)) * 100));
  const disPct = sec.discrete_target > 0
    ? Math.min(100, Math.round((sec.discrete_ready / Math.max(1, sec.discrete_target)) * 100))
    : 100;
  const carsQ = sec.questions_available;
  return (
    <div className="pm-card p-4" data-testid={`pt-prep-section-${sec.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="pm-badge inline-block" style={{ background: SECTION_META[sec.id]?.tint, color: "white" }}>
            {SECTION_META[sec.id]?.short}
          </div>
          <div className="font-display font-bold text-sm mt-1">{sec.name}</div>
        </div>
        {sec.ready ? (
          <CheckCircle2 size={22} style={{ color: "var(--primary)" }} data-testid={`pt-prep-ready-${sec.id}`} />
        ) : (
          <Loader2 size={20} className="animate-spin" style={{ color: "var(--text-muted)" }} />
        )}
      </div>

      <div className="space-y-2 text-xs">
        <div>
          <div className="flex justify-between mb-1">
            <span style={{ color: "var(--text-secondary)" }}>Passages</span>
            <span className="font-mono">{sec.passages_ready}/{sec.passages_target}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
            <div className="h-full transition-all" style={{ width: `${passPct}%`, background: SECTION_META[sec.id]?.tint }} />
          </div>
        </div>
        {sec.id === "cars" ? (
          <div>
            <div className="flex justify-between mb-1">
              <span style={{ color: "var(--text-secondary)" }}>Total questions</span>
              <span className="font-mono">{carsQ ?? 0}/{sec.count_target}</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
              <div className="h-full transition-all" style={{ width: `${Math.min(100, ((carsQ ?? 0) / sec.count_target) * 100)}%`, background: SECTION_META[sec.id]?.tint }} />
            </div>
          </div>
        ) : (
          <div>
            <div className="flex justify-between mb-1">
              <span style={{ color: "var(--text-secondary)" }}>Discrete Qs</span>
              <span className="font-mono">{sec.discrete_ready}/{sec.discrete_target}</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
              <div className="h-full transition-all" style={{ width: `${disPct}%`, background: SECTION_META[sec.id]?.tint }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionReview({ q, idx, selected, isCorrect }) {
  // Guard: a partially-persisted / malformed question in the review shouldn't
  // crash the whole scoreboard. Skip it silently.
  if (!q || !Array.isArray(q.choices) || q.choices.length === 0) {
    return null;
  }
  return (
    <div className="pm-card p-5" data-testid={`pt-review-q-${idx}`}>
      {q.passage_text && (
        <details className="mb-3">
          <summary className="text-xs uppercase tracking-widest font-semibold cursor-pointer" style={{ color: "var(--hl-blue)" }}>
            Passage — {q.passage_title || "context"}
          </summary>
          <div className="text-xs leading-relaxed whitespace-pre-line mt-2" style={{ color: "var(--text-secondary)" }}>
            {q.passage_text}
          </div>
          <FigureView figures={q.figures} idPrefix={`pt-review-figure-${idx}`} />
        </details>
      )}
      <div className="flex items-start gap-2 mb-3">
        <span
          className="pm-badge shrink-0"
          style={{
            background: isCorrect ? "var(--primary)" : "var(--weakness)",
            color: "white",
          }}
        >
          {isCorrect ? "Correct" : "Missed"}
        </span>
        <div className="font-medium text-sm">Q{idx + 1}. {q.question}</div>
      </div>
      <div className="space-y-1.5 mb-3">
        {q.choices.map((c, i) => {
          const isRight = i === q.correct;
          const isSelected = i === selected;
          let bg = "var(--paper)";
          let border = "var(--border)";
          let icon = null;
          if (isRight) { bg = "#e8f2e0"; border = "var(--primary)"; icon = "✓"; }
          if (isSelected && !isRight) { bg = "#fdecea"; border = "var(--weakness)"; icon = "✗"; }
          return (
            <div
              key={i}
              className="text-sm p-2.5 rounded-lg border flex items-center gap-2"
              style={{ background: bg, borderColor: border }}
              data-testid={`pt-review-q-${idx}-choice-${i}`}
            >
              <span className="font-bold w-5">{"ABCD"[i]}.</span>
              <span className="flex-1">{c}</span>
              {icon && <span className="font-bold">{icon}</span>}
            </div>
          );
        })}
      </div>
      {q.explanation && (
        <div className="text-sm p-3 rounded-lg" style={{ background: "var(--muted)", color: "var(--text-secondary)" }}>
          <span className="font-semibold" style={{ color: "var(--text)" }}>Explanation:</span> {q.explanation}
        </div>
      )}
    </div>
  );
}

export default function PracticeTest() {
  const nav = useNavigate();
  const [variant, setVariant] = useState(null);
  const [testNumber, setTestNumber] = useState(1);
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [status, setStatus] = useState(null); // status response from /practice-test/status
  const [mode, setMode] = useState("half"); // "full" | "half" — default to half so Free users see their attempt
  const [timeMultiplier, setTimeMultiplier] = useState(1); // 0 = untimed, 1 = standard, 2 = 2x, 3 = 3x
  const [features, setFeatures] = useState(null); // plan features: full_test, half_test_attempts_remaining
  const [secIdx, setSecIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [strikes, toggleStrike] = useStrikes(attemptId ? `pt-${attemptId}` : null);
  const [showResult, setShowResult] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [resultId, setResultId] = useState(null);
  const [remaining, setRemaining] = useState(0);
  // 10-minute break between sections (real MCAT experience). Only shown
  // in `full` mode — half-length tests skip the ceremony. `onBreak` holds
  // the ID of the section coming up so we can show the "up next" label.
  const [onBreak, setOnBreak] = useState(null); // next-section-id or null
  const [breakRemaining, setBreakRemaining] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/billing/features").then((r) => {
      setFeatures(r.data);
      // Default to Full for paid users, Half for Free — mirrors their entitlement.
      if (r.data?.full_test) setMode("full");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!test || showResult || test.untimed || onBreak) return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [test, showResult, secIdx, onBreak]);

  // Break countdown. When it hits 0 we auto-resume just like the real MCAT.
  useEffect(() => {
    if (!onBreak) return;
    const t = setInterval(() => setBreakRemaining((r) => {
      if (r <= 1) {
        clearInterval(t);
        resumeAfterBreak();
        return 0;
      }
      return r - 1;
    }), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBreak]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const fetchStatus = async (v, m) => {
    const r = await api.get(`/practice-test/status?variant=${v}&mode=${m}`);
    setStatus(r.data);
    return r.data;
  };

  const kickOffPrepPolling = async (v, m) => {
    setPreparing(true);
    const initial = await fetchStatus(v, m);
    // Trigger prepare on any non-ready section
    for (const s of initial.sections) {
      if (!s.ready) {
        api.post("/practice-test/prepare", { section_id: s.id, variant: v, mode: m }).catch(() => {});
      }
    }
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const st = await fetchStatus(v, m);
      for (const s of st.sections) {
        if (!s.ready && !st.priming_active[s.id]) {
          api.post("/practice-test/prepare", { section_id: s.id, variant: v, mode: m }).catch(() => {});
        }
      }
      if (st.ready) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 8000);
  };

  const beginPrep = async (v) => {
    setVariant(v);
    await kickOffPrepPolling(v, mode);
  };

  const launchTest = async () => {
    if (!variant) return;
    setLoading(true);
    try {
      const r = await api.post("/practice-test/start", { variant, test_number: testNumber, mode, time_multiplier: timeMultiplier });
      setTest(r.data);
      setAttemptId(`${Date.now()}`);
      // Untimed → keep remaining at 0; the timer UI reads test.untimed to hide.
      const firstMin = r.data.sections[0]?.minutes;
      setRemaining(firstMin ? firstMin * 60 : 0);
      setSecIdx(0); setQIdx(0); setAnswers({}); setFlags({}); setShowResult(false);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "sections_not_ready") {
        alert("Sections still preparing — please wait a moment.");
      } else if (detail?.error === "feature_locked") {
        alert(detail.reason || "This practice test is a Plus feature. Upgrade to unlock.");
        nav("/app/plans");
      } else {
        alert("Failed to start test.");
      }
    } finally {
      setLoading(false);
    }
  };

  const section = test?.sections?.[secIdx];
  const question = section?.questions?.[qIdx];
  const key = `${secIdx}-${qIdx}`;

  const nextQ = () => {
    if (!section) return;
    if (qIdx < section.questions.length - 1) {
      setQIdx(qIdx + 1);
    } else if (secIdx < test.sections.length - 1) {
      const nextSec = secIdx + 1;
      // Real MCAT gives you a 10-minute break between sections. We only
      // show it in `full` mode — half-length practice is meant to be quick.
      if (mode === "full" && !test.untimed) {
        setOnBreak(test.sections[nextSec].id);
        setBreakRemaining(600); // 10 minutes
        return;
      }
      setSecIdx(nextSec); setQIdx(0);
      const nextMin = test.sections[nextSec]?.minutes;
      setRemaining(nextMin ? nextMin * 60 : 0);
    } else {
      const scores = {};
      test.sections.forEach((s, i) => {
        const c = s.questions.filter((q, j) => answers[`${i}-${j}`] === q.correct).length;
        scores[s.id] = { correct: c, total: s.questions.length };
      });
      // Persist full test payload so we can show explanations + PDF later
      api.post("/practice-test/submit", {
        variant,
        mode,
        section_scores: scores,
        sections: test.sections,
        answers,
      }).then((r) => setResultId(r?.data?.result_id || null)).catch(() => {});
      setShowResult(true);
    }
  };

  const prevQ = () => {
    // Walk back within a section, or hop to the previous section's last
    // question. No timer reset — students shouldn't lose section time just
    // for reviewing. First question of first section = no-op.
    if (!section) return;
    if (qIdx > 0) {
      setQIdx(qIdx - 1);
    } else if (secIdx > 0) {
      const prevSec = secIdx - 1;
      const prevSection = test.sections[prevSec];
      setSecIdx(prevSec);
      setQIdx(Math.max(0, (prevSection?.questions?.length || 1) - 1));
    }
  };

  const resumeAfterBreak = () => {
    // Advance to the next section — same code path as the pre-break nextQ.
    const nextSec = secIdx + 1;
    setOnBreak(null);
    setBreakRemaining(0);
    setSecIdx(nextSec);
    setQIdx(0);
    const nextMin = test?.sections?.[nextSec]?.minutes;
    setRemaining(nextMin ? nextMin * 60 : 0);
  };

  const canGoBack = secIdx > 0 || qIdx > 0;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  // ---------- Preparing screen ----------
  if (preparing && !test) {
    const totalReady = status?.sections.filter((s) => s.ready).length ?? 0;
    const allReady = status?.ready;
    return (
      <div className="max-w-4xl mx-auto" data-testid="pt-prep-page">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold mb-2">Preparing your MCAT</h1>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              We&apos;re AI-generating passages and discrete questions to hit exact AAMC counts ({mode === "half" ? "30/26/30/29 = 115" : "59/53/59/59 = 230"}).
              This runs in the background — you can leave and come back. First time takes a few minutes; subsequent tests are instant.
            </p>
          </div>
          <div className="pm-badge" style={{ background: "var(--muted)" }} data-testid="pt-prep-ready-count">
            {totalReady}/4 sections ready
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {(status?.sections ?? []).map((s) => <SectionProgress key={s.id} sec={s} />)}
        </div>

        <div className="flex items-center justify-between pm-card p-4">
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            <Sparkles size={16} /> Priming with Claude Sonnet 4.5
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setPreparing(false); setStatus(null); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }}
              className="pm-btn-secondary text-sm"
              data-testid="pt-prep-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={launchTest}
              disabled={!allReady || loading}
              className="pm-btn-primary text-sm flex items-center gap-2"
              style={{ opacity: allReady ? 1 : 0.5, cursor: allReady ? "pointer" : "not-allowed" }}
              data-testid="pt-prep-launch-btn"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : allReady ? "Begin Test" : "Waiting..."}
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Intro screen ----------
  if (!test) {
    const isHalf = mode === "half";
    const headline = isHalf
      ? "Exactly 115 questions — 30 C/P + 26 CARS + 30 B/B + 29 P/S — modeled on the MCAT's timing, section structure, and passage-based format (half-length)."
      : "Exactly 230 questions — 59 C/P + 53 CARS + 59 B/B + 59 P/S — modeled on the MCAT's timing, section structure, and passage-based format.";
    const sectionsLine = isHalf
      ? "Sections: Chem/Physics (30 Qs, 47 min) → CARS (26 Qs, 45 min) → Bio/Biochem (30 Qs, 47 min) → Psych/Socio (29 Qs, 47 min)."
      : "Sections: Chem/Physics (59 Qs, 95 min) → CARS (53 Qs, 90 min) → Bio/Biochem (59 Qs, 95 min) → Psych/Socio (59 Qs, 95 min).";
    return (
      <div className="max-w-3xl mx-auto" data-testid="pt-intro-page">
        <h1 className="font-display text-4xl font-bold mb-3">
          {isHalf ? "Half-Length Practice Test" : "Full-Length Practice Test"}
        </h1>
        <p className="text-lg mb-4" style={{ color: "var(--text-secondary)" }}>
          {headline}
        </p>

        {/* Mode picker */}
        <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="pt-mode-picker">
          <span className="text-sm self-center" style={{ color: "var(--text-muted)" }}>Length:</span>
          {[
            { id: "full", label: "Full (230)", desc: "~7.5 hours", locked: features && !features.full_test },
            { id: "half", label: "Half (115)", desc: "~3 hours", locked: false },
          ].map((m) => (
            <button
              key={m.id}
              onClick={() => {
                if (m.locked) {
                  nav("/app/plans");
                  return;
                }
                setMode(m.id);
              }}
              className="pm-badge inline-flex items-center gap-1"
              style={{
                background: mode === m.id ? "var(--primary)" : "var(--muted)",
                color: mode === m.id ? "white" : "var(--text)",
                cursor: "pointer",
                padding: "6px 14px",
                opacity: m.locked ? 0.75 : 1,
              }}
              data-testid={`pt-mode-${m.id}`}
              title={m.locked ? "Full-length is a Plus feature — click to upgrade." : m.desc}
            >
              {m.locked && <Lock size={12} />}
              {m.label}
            </button>
          ))}
        </div>
        {/* Plan gating hint */}
        {features && (
          <div className="mb-4 text-xs" style={{ color: "var(--text-muted)" }} data-testid="pt-gating-hint">
            {features.full_test ? (
              <span>Both lengths unlimited on {features.plan_name}. Take as many as you like.</span>
            ) : mode === "half" ? (
              <span>
                Free plan includes <b>{features.half_test_attempts} half-length practice test</b>
                {features.half_test_attempts_remaining !== undefined && (
                  <> · <span style={{ color: features.half_test_attempts_remaining > 0 ? "var(--primary)" : "var(--weakness)" }}>
                    {features.half_test_attempts_remaining} attempt{features.half_test_attempts_remaining === 1 ? "" : "s"} remaining
                  </span></>
                )}
                . Upgrade to Plus for unlimited attempts + full-length tests.
              </span>
            ) : (
              <span>Full-length is Plus-only — <button onClick={() => nav("/app/plans")} className="underline" style={{ color: "var(--primary)" }} data-testid="pt-upgrade-link">upgrade to unlock</button>.</span>
            )}
          </div>
        )}

        {/* Timing picker */}
        <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="pt-timing-picker">
          <span className="text-sm self-center" style={{ color: "var(--text-muted)" }}>Timing:</span>
          {[
            { id: 1, label: "Standard", desc: "Real MCAT pace — 95 min per science section, 90 min CARS" },
            { id: 1.5, label: "1.5× Time", desc: "1.5× the standard section time" },
            { id: 2, label: "2× Time", desc: "Double time (extended)" },
            { id: 0, label: "Unlimited", desc: "No timer — pause and think as long as you want" },
          ].map((tm) => (
            <button
              key={tm.id}
              onClick={() => setTimeMultiplier(tm.id)}
              className="pm-badge inline-flex items-center gap-1"
              style={{
                background: timeMultiplier === tm.id ? "var(--primary)" : "var(--muted)",
                color: timeMultiplier === tm.id ? "white" : "var(--text)",
                cursor: "pointer",
                padding: "6px 14px",
              }}
              data-testid={`pt-timing-${tm.id}`}
              title={tm.desc}
            >
              {tm.label}
            </button>
          ))}
        </div>

        <div className="flex gap-2 mb-6" data-testid="pt-test-picker">
          <span className="text-sm self-center" style={{ color: "var(--text-muted)" }}>Test:</span>
          {[1,2,3,4,5].map(n => (
            <button
              key={n}
              onClick={() => setTestNumber(n)}
              className="w-9 h-9 rounded-full font-medium text-sm"
              style={{ background: testNumber === n ? "var(--primary)" : "var(--muted)", color: testNumber === n ? "white" : "var(--text)" }}
              data-testid={`pt-test-num-${n}`}
            >
              {n}
            </button>
          ))}
          <span className="text-xs self-center ml-2" style={{ color: "var(--text-muted)" }}>Each # shuffles differently.</span>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {[
            { id: "content", title: "Content Exposure", desc: "Recall + terminology. Great first pass — tests raw knowledge.", color: "var(--primary)" },
            { id: "application", title: "Application Exposure", desc: "MCAT-style scenarios, experiments, and passage reasoning. Tests applying concepts.", color: "var(--accent)" },
          ].map((v) => (
            <button
              key={v.id}
              onClick={() => beginPrep(v.id)}
              disabled={preparing}
              className="pm-card pm-card-hover p-6 text-left"
              data-testid={`pt-start-${v.id}`}
            >
              <div className="pm-badge mb-3" style={{ background: v.color, color: "white" }}>{v.title}</div>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{v.desc}</p>
              <div className="mt-4 text-sm font-medium flex items-center gap-1" style={{ color: v.color }}>
                Prepare & Begin <ArrowRight size={14} />
              </div>
            </button>
          ))}
        </div>
        <div className="mt-6 text-sm p-4 rounded-xl" style={{ background: "var(--muted)", color: "var(--text-secondary)" }}>
          {sectionsLine} First launch primes the question bank with AI — sit tight for a few minutes on the first run only.
        </div>
        <p className="mt-4 text-xs" style={{ color: "var(--text-muted)" }} data-testid="pt-aamc-disclaimer">
          PictureMed is not affiliated with, endorsed by, or sponsored by the
          Association of American Medical Colleges (AAMC). MCAT® is a registered
          trademark of the AAMC. Practice content is modeled on the exam's
          publicly documented timing and section structure.
        </p>
      </div>
    );
  }

  // ---------- Review Answers ----------
  if (showReview && test) {
    return (
      <div className="max-w-4xl mx-auto" data-testid="pt-review-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--text-muted)" }}>
              Answer Review
            </div>
            <h1 className="font-display text-3xl font-bold">Every question, explained</h1>
          </div>
          <button
            onClick={() => setShowReview(false)}
            className="pm-btn-secondary text-sm"
            data-testid="pt-review-back-btn"
          >
            Back to score
          </button>
        </div>

        {test.sections.map((s, i) => (
          <div key={s.id} className="mb-8" data-testid={`pt-review-section-${s.id}`}>
            <h2 className="font-display font-bold text-xl mb-3" style={{ color: "var(--primary)" }}>
              {s.name}
            </h2>
            <div className="space-y-4">
              {s.questions.map((q, j) => {
                const selected = answers[`${i}-${j}`];
                const isCorrect = selected === q.correct;
                return (
                  <QuestionReview
                    key={`${i}-${j}`}
                    q={q}
                    idx={j}
                    selected={selected}
                    isCorrect={isCorrect}
                  />
                );
              })}
            </div>
          </div>
        ))}

        <div className="text-center mt-8">
          <button
            onClick={() => setShowReview(false)}
            className="pm-btn-primary"
            data-testid="pt-review-done-btn"
          >
            Done reviewing
          </button>
        </div>
      </div>
    );
  }

  // ---------- Results ----------
  if (showResult) {
    const scores = test.sections.map((s, i) => {
      const c = s.questions.filter((q, j) => answers[`${i}-${j}`] === q.correct).length;
      return { name: s.name, correct: c, total: s.questions.length, scaled: scaledScore(c, s.questions.length) };
    });
    const allFour = test.sections.length === 4;
    const scaledTotal = scores.reduce((a, b) => a + b.scaled, 0);
    const totC = scores.reduce((a, b) => a + b.correct, 0);
    const totT = scores.reduce((a, b) => a + b.total, 0);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    return (
      <div className="max-w-2xl mx-auto text-center" data-testid="pt-results">
        <div className="text-6xl mb-4">🎓</div>
        <h1 className="font-display text-4xl font-bold mb-3">Test Complete</h1>
        <div className="font-display text-6xl font-bold mb-8" style={{ color: "var(--primary)" }}>
          {totC}/{totT}
        </div>
        {allFour && (
          <div className="mb-8 -mt-4" data-testid="pt-scaled-total">
            <div className="font-display text-3xl font-bold">{scaledTotal}<span className="text-base font-normal" style={{ color: "var(--text-muted)" }}> / 528 est.</span></div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>Unofficial estimate from raw accuracy (472–528 scale). Not an AAMC score.</div>
          </div>
        )}
        <div className="space-y-3 text-left">
          {scores.map((s, i) => (
            <div key={i} className="pm-card p-4 flex items-center justify-between" data-testid={`pt-section-score-${i}`}>
              <div className="font-medium">{s.name}</div>
              <div className="font-mono text-right">
                <div>{s.correct}/{s.total}</div>
                {allFour && <div className="text-xs" style={{ color: "var(--text-muted)" }} data-testid={`pt-section-scaled-${i}`}>≈ {s.scaled} / 132</div>}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          <button
            onClick={() => setShowReview(true)}
            className="pm-btn-primary"
            data-testid="pt-review-answers-btn"
          >
            Review answers
          </button>
          <a
            href={resultId ? `${backendUrl}/api/practice-test/results/${resultId}/pdf` : "#"}
            target="_blank"
            rel="noreferrer"
            className={`pm-btn-secondary ${!resultId ? "opacity-50 pointer-events-none" : ""}`}
            data-testid="pt-download-pdf-btn"
          >
            Download PDF study guide
          </a>
          <button
            onClick={() => { setTest(null); setPreparing(false); setStatus(null); setResultId(null); setShowReview(false); }}
            className="pm-btn-secondary"
            data-testid="pt-restart-btn"
          >
            Take another
          </button>
        </div>
      </div>
    );
  }

  if (!question || !Array.isArray(question.choices) || question.choices.length === 0) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <p className="text-lg" style={{ color: "var(--text-secondary)" }}>This section has no questions in the bank yet.</p>
        <button onClick={nextQ} className="pm-btn-primary mt-6" data-testid="pt-skip-section-btn">Skip section</button>
      </div>
    );
  }

  if (onBreak) {
    const nextSection = test.sections.find((s) => s.id === onBreak);
    const bMin = Math.floor(breakRemaining / 60);
    const bSec = String(breakRemaining % 60).padStart(2, "0");
    return (
      <div className="max-w-2xl mx-auto py-10" data-testid="pt-break-screen">
        <div className="pm-card p-8 text-center">
          <div
            className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ background: "var(--muted)" }}
          >
            <Clock size={30} style={{ color: "var(--primary)" }} />
          </div>
          <div
            className="pm-badge mb-3 inline-flex"
            style={{ background: "#c98a3b22", color: "#c98a3b" }}
          >
            Break in progress
          </div>
          <h1
            className="font-display text-4xl font-bold mb-2 tabular-nums"
            data-testid="pt-break-countdown"
          >
            {bMin}:{bSec}
          </h1>
          <p className="mb-6" style={{ color: "var(--text-secondary)" }}>
            Real MCAT gives you a 10-minute break between sections. Stretch,
            hydrate, and reset your focus — you've earned it.
          </p>
          {nextSection && (
            <div
              className="pm-card p-4 mb-6 text-left"
              style={{ background: "var(--paper)" }}
              data-testid="pt-break-next-section"
            >
              <div
                className="text-xs uppercase tracking-widest font-semibold mb-1"
                style={{ color: "var(--text-muted)" }}
              >
                Coming up next — section {secIdx + 2} of {test.sections.length}
              </div>
              <div className="font-display font-bold text-lg">
                {nextSection.name}
              </div>
              {nextSection.minutes && (
                <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  {nextSection.minutes} min · {nextSection.questions?.length || 0} questions
                </div>
              )}
            </div>
          )}
          <button
            onClick={resumeAfterBreak}
            className="pm-btn-primary inline-flex items-center gap-2"
            data-testid="pt-break-skip-btn"
          >
            <ArrowRight size={14} /> Skip break and start next section
          </button>
          <p
            className="text-xs mt-4"
            style={{ color: "var(--text-muted)" }}
          >
            The break will end automatically when the timer hits 0:00.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="pt-question-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--text-muted)" }}>
            Section {secIdx + 1} of {test.sections.length}
          </div>
          <div className="font-display font-bold text-lg">{section.name}</div>
        </div>
        {test.untimed ? (
          <div className="pm-badge flex items-center gap-1.5" style={{ background: "var(--muted)", color: "var(--text-secondary)" }} data-testid="pt-timer-untimed">
            <Clock size={12} /> Untimed
          </div>
        ) : (
          <div className="pm-badge flex items-center gap-1.5" style={{ background: remaining < 300 ? "#fdecea" : "var(--muted)", color: remaining < 300 ? "var(--weakness)" : "var(--text)" }} data-testid="pt-timer">
            <Clock size={12} /> {mm}:{ss}
            {test.time_multiplier && test.time_multiplier > 1 && (
              <span className="ml-1 text-xs opacity-75">({test.time_multiplier}×)</span>
            )}
          </div>
        )}
      </div>

      <div className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
        Question {qIdx + 1} of {section.questions.length}
      </div>

      {question.passage_text && (
        <div className="pm-card p-5 mb-4 max-h-64 overflow-y-auto" data-testid="pt-passage">
          <div className="pm-badge mb-2" style={{ background: "var(--hl-blue)" }}>{question.passage_title}</div>
          <div className="text-sm leading-relaxed">
            <HighlightableText
              text={question.passage_text}
              storageKey={`pt-passage-${question.id || question.question_id || qIdx}`}
              testid="pt-passage-highlights"
            />
          </div>
          <FigureView figures={question.figures} idPrefix="pt-figure" />
        </div>
      )}

      <div className="pm-card p-6">
        <div className="font-medium mb-4" data-testid="pt-question-text">{question.question}</div>
        <div className="space-y-2">
          {question.choices.map((c, i) => (
            <div key={i} className="flex gap-2">
              <button
                onClick={() => setAnswers({ ...answers, [key]: i })}
                className="w-full text-left p-3 rounded-xl border-2 flex items-center gap-3 text-sm"
                style={{
                  borderColor: answers[key] === i ? "var(--primary)" : "var(--border)",
                  background: answers[key] === i ? "var(--muted)" : "var(--paper)",
                }}
                data-testid={`pt-choice-${i}`}
              >
                <span className="font-bold">{["A","B","C","D"][i]}</span>
                <span className="flex-1" style={(strikes[key] || []).includes(i) ? struckStyle : undefined}>{c}</span>
              </button>
              <StrikeButton on={(strikes[key] || []).includes(i)} onClick={() => toggleStrike(key, i)} testid={`pt-strike-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center mt-6 gap-3 flex-wrap">
        <button
          onClick={prevQ}
          disabled={!canGoBack}
          className="pm-btn-secondary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pt-back-btn"
          title={canGoBack ? "Go back to review or change your previous answer" : "This is the first question"}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setFlags({ ...flags, [key]: !flags[key] })} className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-xl"
            style={{ background: flags[key] ? "#e08a2a" : "var(--muted)", color: flags[key] ? "white" : "var(--text-secondary)" }} data-testid="pt-flag-btn">
            <Flag size={14} /> {flags[key] ? "Flagged" : "Flag"}
          </button>
          <button onClick={() => setReviewOpen(true)} className="pm-btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="pt-review-screen-btn">
            <ListChecks size={14} /> Review
          </button>
        </div>
        <ReviewScreen open={reviewOpen} onOpenChange={setReviewOpen} title={`Review screen · ${section.name}`} onJump={setQIdx}
          items={section.questions.map((qq, i) => ({ index: i, label: qq.question, answered: answers[`${secIdx}-${i}`] != null, flagged: !!flags[`${secIdx}-${i}`] }))} />
        <div className="flex items-center gap-3">
          {answers[key] != null && (
            <span
              className="text-xs inline-flex items-center gap-1"
              style={{ color: "var(--text-muted)" }}
              data-testid="pt-answered-hint"
            >
              <CheckCircle2 size={12} style={{ color: "var(--primary)" }} />
              Answered — tap Back anytime to change it
            </span>
          )}
          <button
            onClick={() => {
              const isFinish = qIdx === section.questions.length - 1 && secIdx === test.sections.length - 1;
              if (isFinish) {
                // Warn if some answers are missing so students don't submit blind.
                const total = test.sections.reduce((n, s) => n + s.questions.length, 0);
                const answered = Object.keys(answers).length;
                if (answered < total) {
                  const ok = window.confirm(
                    `You've answered ${answered} of ${total} questions. ` +
                    "Submit anyway? (Unanswered questions count as wrong.)"
                  );
                  if (!ok) return;
                }
              }
              nextQ();
            }}
            className="pm-btn-primary flex items-center gap-2"
            data-testid="pt-next-btn"
          >
            {qIdx === section.questions.length - 1 && secIdx === test.sections.length - 1 ? "Finish" : "Next"}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
